import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetSwitch } from '../SettingsShell/controls';
import { arFollowUpStore } from '../../ar/mockData';

export interface ArSettingsViewProps {
  onOpenFollowUps?: () => void;
}

/**
 * Settings › Accounts receivable — **rebuilt** (amendment 28 §6, board `AR6Settings`).
 *
 * Removed, all of it: pools, lanes, default step timing, the overrides table,
 * *Sequence →*, *Add a pool*, every explanatory paragraph. Who gets followed up
 * and what they get is set per follow-up, not here. AI calls **stay** — the
 * practice is calling its own patients, who agreed to calls — so 02 keeps the
 * call switch and the over-$1,000 ceiling.
 */
export function ArSettingsView({ onOpenFollowUps }: ArSettingsViewProps) {
  const active = arFollowUpStore.followUps.filter((f) => f.state === 'active').length;
  const drafts = arFollowUpStore.followUps.filter((f) => f.state === 'draft').length;
  const [floor, setFloor] = useState(25);
  const [ceiling, setCeiling] = useState(1000);
  const [mayCall, setMayCall] = useState(true);

  return (
    <div className="min-w-0 flex-1">
      <SettingsPageHeader
        eyebrow="Settings · AI Brain"
        titleLead="Accounts"
        titleAccent="receivable"
        sub="Follows up balances patients owe, by text, email and call, with an ELVA Pay link in every message."
      />

      {/* banner — the settings that matter live in each follow-up */}
      <div className="mb-[26px] flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[var(--border-default)] bg-[var(--brand-indigo-tint)] px-4 py-3">
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-[var(--text-primary)]">Who gets followed up, and what they get, is set in each follow-up</span>
          <span className="block text-[11.5px] text-[var(--text-muted)]">Collections › Follow-ups · {active} active, {drafts} draft</span>
        </span>
        <Button variant="primary" size="sm" onClick={onOpenFollowUps}>Open follow-ups</Button>
      </div>

      <SettingsCards>
        {/* 01 — always applied */}
        <SettingsCard n="01" title="Always applied" sub="Checked for every follow-up, every patient, before every message and call.">
          {ALWAYS.map((a) => (
            <div key={a.label} className="flex items-start gap-3 px-6 py-3.5" style={{ borderBottom: '1px solid #edf0f6' }}>
              <Icon kind="lock" size={13} stroke={2} className="mt-0.5 shrink-0 text-[var(--text-muted)]" />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-[var(--text-primary)]">{a.label}</span>
                <span className="mt-0.5 block text-[11.5px] text-[var(--text-muted)]">{a.sub}</span>
              </span>
            </div>
          ))}
          <SettingsRow
            last
            label="Don’t follow up anything under"
            desc="You can raise this, not lower it — $25 is ELVA’s floor."
            control={
              <MoneyInput
                value={floor}
                onChange={setFloor}
                invalid={floor < 25}
                invalidMsg="$25 is ELVA’s floor — you can raise it, not lower it."
              />
            }
          />
        </SettingsCard>

        {/* 02 — calls about money */}
        <SettingsCard n="02" title="Calls about money" sub="ELVA may call about a balance. Above an amount, a person makes the call instead.">
          <SettingsRow
            label="ELVA may call about a balance"
            desc="Only patients who agreed to calls from the practice, inside your call hours. The AI Receptionist places the call."
            control={<SetSwitch label="ELVA may call about a balance" checked={mayCall} onChange={setMayCall} />}
          />
          <SettingsRow
            label="A person makes the call above"
            desc="The texts and emails still go; the call becomes a task in Triage. You can lower this, not raise it — $1,000 is ELVA’s ceiling."
            control={
              <MoneyInput
                value={ceiling}
                onChange={setCeiling}
                invalid={ceiling > 1000}
                invalidMsg="$1,000 is ELVA’s ceiling — you can lower it, not raise it."
              />
            }
          />
          <SettingsRow
            last
            label="Money tasks go to"
            desc="Replies, disputes and call tasks."
            control={
              <select defaultValue="billing" className="rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2.5 py-1.5 text-[12.5px] font-semibold text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]">
                <option value="billing">Billing coordinator</option>
                <option value="office">Office manager</option>
                <option value="frontdesk">Front desk</option>
              </select>
            }
          />
        </SettingsCard>

        {/* 03 — payment links */}
        <SettingsCard n="03" title="Payment links" sub="Made by ELVA Pay. The payment page, cards and receipts are ELVA Pay’s.">
          <SettingsRow
            label="ELVA Pay"
            desc="The account payment links are paid into."
            control={
              <span className="inline-flex items-center gap-2">
                <span className="rounded-[6px] bg-[var(--money-green-tint)] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-[var(--money-green)]">Connected</span>
                <a href="#" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">Open ELVA Pay</a>
              </span>
            }
          />
          <SettingsRow
            label="Payment plans"
            desc="When plans are on in ELVA Pay, messages can say a plan is possible. ELVA never sets one up."
            control={
              <span className="inline-flex items-center gap-2">
                <span className="rounded-[6px] bg-[var(--chip-fill)] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-[var(--text-muted)]">Off</span>
                <a href="#" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">Turn on in ELVA Pay</a>
              </span>
            }
          />
          <SettingsRow
            last
            label="When a patient pays"
            desc="The payment is written to their balance in the PMS."
            control={<span className="text-[12px] font-semibold text-[var(--text-secondary)]">Posted to Ascend automatically</span>}
          />
        </SettingsCard>

        {/* 04 — never, on any channel */}
        <SettingsCard n="04" title="Never, on any channel" sub="Locked for every practice.">
          {NEVER.map((label, i) => (
            <SettingsRow
              key={label}
              last={i === NEVER.length - 1}
              label={<span className="inline-flex items-center gap-1.5"><Icon kind="lock" size={11} stroke={2} className="text-[var(--text-muted)]" />{label}</span>}
              control={<span className="text-[12px] font-bold text-[var(--text-muted)]">Never</span>}
            />
          ))}
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter location="Settings · AI Brain · Accounts receivable" />
    </div>
  );
}

const ALWAYS = [
  { label: 'The insurer has finished with it', sub: 'No open claim, no denial that can be appealed. Those balances are never asked of the patient.' },
  { label: 'Not on a payment plan that’s up to date', sub: 'A plan that falls behind comes back at its new size.' },
  { label: 'Not disputed, opted out, at an agency, deceased or in bankruptcy', sub: 'A person clears these.' },
  { label: 'Can be contacted', sub: 'A mobile or email. Calls only to patients who agreed to calls.' },
  { label: 'No appointment in the next 14 days', sub: 'The desk collects at the visit.' },
  { label: 'The balance has been theirs for 7 days or more', sub: 'Time for your own statement to arrive first.' },
];

const NEVER = [
  'Threaten anything',
  'Use the word collections',
  'Mention credit, or a credit score',
  'Mention a lawyer, a court or a debt agency',
  'Say treatment will be held back',
];

function MoneyInput({ value, onChange, invalid, invalidMsg }: { value: number; onChange: (n: number) => void; invalid: boolean; invalidMsg: string }) {
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex items-center rounded-[9px] border border-[var(--border-default)] px-2 py-1">
        <span className="text-[12.5px] text-[var(--text-muted)]">$</span>
        <input
          type="number"
          value={value}
          onChange={(e) => { onChange(Number(e.target.value)); }}
          className="w-[64px] bg-transparent text-right font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)] outline-none"
        />
      </span>
      {invalid ? <span className="text-[10.5px] font-semibold text-[var(--alert-red)]">{invalidMsg}</span> : null}
    </span>
  );
}
